import { supabase } from './supabase'

/**
 * Image Storage Service
 * Downloads external images and stores them in Supabase Storage
 * Serves them through Supabase's CDN
 */
export class ImageStorageService {
  private readonly BUCKET_NAME = 'product-images'
  
  /**
   * Download an external image and upload it to Supabase Storage
   * @param imageUrl - The external image URL to download
   * @param contributionId - The contribution ID (used for filename)
   * @returns The Supabase CDN URL for the stored image
   * @throws Error if image cannot be cached
   */
  async cacheImageToSupabase(imageUrl: string, contributionId?: string): Promise<string> {
    try {
      console.log('Downloading image from:', imageUrl)
      
      // Download the image
      const response = await fetch(imageUrl)
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.statusText}`)
      }
      
      const blob = await response.blob()
      const contentType = response.headers.get('content-type') || 'image/jpeg'
      
      // Generate a unique filename
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(7)
      const extension = this.getExtensionFromMimeType(contentType)
      const fileName = contributionId 
        ? `contributions/${contributionId}/${timestamp}.${extension}`
        : `temp/${randomId}-${timestamp}.${extension}`
      
      console.log('Uploading to Supabase Storage:', fileName)
      
      // Upload to Supabase Storage
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(fileName, blob, {
          contentType,
          cacheControl: '31536000', // Cache for 1 year
          upsert: false
        })
      
      if (error) {
        console.error('Upload error:', error)
        throw error
      }
      
      // Get the public URL (served through Supabase CDN)
      const { data: { publicUrl } } = supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(fileName)
      
      console.log('Image cached successfully:', publicUrl)
      return publicUrl
      
    } catch (error) {
      console.error('Failed to cache image to Supabase:', error)
      // Throw error - no fallback to remote URL
      throw new Error(`Failed to cache image: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
  
  /**
   * Delete an image from Supabase Storage
   * @param filePath - The file path in the storage bucket
   */
  async deleteImage(filePath: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([filePath])
      
      if (error) throw error
      console.log('Image deleted:', filePath)
    } catch (error) {
      console.error('Failed to delete image:', error)
      throw error
    }
  }
  
  /**
   * Get a signed URL for temporary access (useful for private images)
   * @param filePath - The file path in the storage bucket
   * @param expiresIn - Expiration time in seconds (default: 1 hour)
   */
  async getSignedUrl(filePath: string, expiresIn: number = 3600): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .createSignedUrl(filePath, expiresIn)
      
      if (error) throw error
      return data?.signedUrl || null
    } catch (error) {
      console.error('Failed to create signed URL:', error)
      return null
    }
  }
  
  /**
   * Cache multiple images to Supabase Storage
   * @param images - Array of image URLs to cache
   * @param contributionId - The contribution ID (used for organizing files)
   * @returns Array of cached image objects with both original and cached URLs
   */
  async cacheMultipleImages(
    images: Array<{ url: string; alt?: string; order?: number; cached_url?: string; width?: number; height?: number }>,
    contributionId: string
  ): Promise<Array<{ url: string; cached_url: string; alt?: string; order: number; width?: number; height?: number }>> {
    const cachedImages = []
    
    for (let index = 0; index < images.length; index++) {
      const image = images[index]
      try {
        const cachedUrl = await this.cacheImageToSupabase(image.url, contributionId)
        cachedImages.push({
          ...image,
          url: image.url, // Keep original for reference
          cached_url: cachedUrl, // Add cached URL
          order: image.order ?? index // Ensure order is always a number
        })
      } catch (error) {
        console.error(`Failed to cache image ${image.url} (likely CORS), using original URL:`, error)
        // Use original URL as fallback when caching fails
        cachedImages.push({
          ...image,
          url: image.url, // Keep original for reference
          cached_url: image.url, // Use original URL as fallback
          order: image.order ?? index // Ensure order is always a number
        })
      }
    }
    
    // This should never happen now that we use fallbacks, but keeping as safety check
    if (cachedImages.length === 0 && images.length > 0) {
      throw new Error('Failed to process any images')
    }
    
    return cachedImages
  }
  
  /**
   * Helper to get file extension from MIME type
   */
  private getExtensionFromMimeType(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg',
      'image/avif': 'avif'
    }
    
    return mimeToExt[mimeType] || 'jpg'
  }
  
  /**
   * Create the storage bucket if it doesn't exist
   * Run this once during setup
   */
  async createBucketIfNotExists(): Promise<void> {
    try {
      const { data: buckets, error: listError } = await supabase.storage.listBuckets()
      
      if (listError) {
        console.warn('Could not list buckets (may need admin permissions):', listError)
        // Try to proceed anyway - bucket might already exist
        return
      }
      
      const bucketExists = buckets?.some(bucket => bucket.name === this.BUCKET_NAME)
      
      if (!bucketExists) {
        const { error: createError } = await supabase.storage.createBucket(this.BUCKET_NAME, {
          public: true, // Public bucket for CDN access
          fileSizeLimit: 5242880, // 5MB max file size
          allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif']
        })
        
        if (createError) {
          console.warn('Could not create bucket (may already exist or need admin permissions):', createError)
          // Continue anyway - bucket might already exist
        } else {
          console.log('Storage bucket created:', this.BUCKET_NAME)
        }
      } else {
        console.log('Storage bucket already exists:', this.BUCKET_NAME)
      }
    } catch (error) {
      console.warn('Bucket creation check failed, continuing anyway:', error)
      // Don't throw - let the actual upload operations fail if bucket doesn't exist
    }
  }
}

// Export singleton instance
export const imageStorage = new ImageStorageService()